import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';

const MASTER_DATA_API = '/api/master-data';
const DEPARTMENTS_API = '/api/departments';
const PREDEFINED_REPLIES_API = '/api/predefined-replies';
const SERVICES_API = '/api/services';

const Icon = ({ d, size = 16, strokeWidth = 1.75 }) => (
    <svg
        width={size} height={size} viewBox="0 0 24 24"
        fill="none" stroke="currentColor"
        strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round"
    >
        {Array.isArray(d) ? d.map((p, i) => <path key={i} d={p} />) : <path d={d} />}
    </svg>
);

const icons = {
    priority: ['M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6'],
    department: ['M3 21h18', 'M9 8h6', 'M9 12h6', 'M9 16h6', 'M4 21V5a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v16'],
    plus: ['M12 5v14', 'M5 12h14'],
    search: ['M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16z', 'M21 21l-4.35-4.35'],
    edit: ['M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7', 'M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z'],
    trash: ['M3 6h18', 'M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2'],
    x: ['M18 6 6 18', 'M6 6l12 12'],
    alertTriangle: ['M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z', 'M12 9v4', 'M12 17h.01'],
    flag: ['M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z', 'M4 22v-7'],
    briefcase: ['M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16', 'M2 21h20', 'M8 7h8'],
    user: ['M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2', 'M12 3a4 4 0 1 0 0 8 4 4 0 0 0 0-8z'],
    chevronDown: ['M6 9l6 6 6-6'],
    messageSquare: ['M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z'],
    bold: ['M6 4h8a4 4 0 0 1 4 4 4 4 0 0 1-4 4H6zm0 8h9a4 4 0 0 1 4 4 4 4 0 0 1-4 4H6z'],
    italic: ['M19 4h-9', 'M14 20H5', 'M15 4L9 20'],
    alignLeft: ['M21 6H3', 'M15 12H3', 'M17 18H3'],
    alignCenter: ['M21 6H3', 'M19 12H5', 'M17 18H7'],
    alignRight: ['M21 6H3', 'M21 12H9', 'M21 18H11'],
    alignJustify: ['M21 6H3', 'M21 12H3', 'M21 18H3'],
    link: ['M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71', 'M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71'],
    image: ['M3 3h18v18H3z', 'M3 15l5.12-5.12a3 3 0 0 1 4.24 0L15 12', 'M11 8a2 2 0 1 1-4 0 2 2 0 0 1 4 0'],
    moreHorizontal: ['M12 12h.01', 'M19 12h.01', 'M5 12h.01'],
    font: ['M4 20h16', 'M7 16l4.4-12h1.2l4.4 12', 'M8 12h8'],
    pencil: ['M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z'],
    list: ['M8 6h13', 'M8 12h13', 'M8 18h13', 'M3 6h.01', 'M3 12h.01', 'M3 18h.01'],
    wrench: ['M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z'],
};

/* ── Modern Color Picker Component ── */
function ModernColorPicker({ color, onChange }) {
    const [open, setOpen] = useState(false);
    const pickerRef = React.useRef(null);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (pickerRef.current && !pickerRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleWheelClick = (e) => {
        const rect = e.currentTarget.getBoundingClientRect();
        const x = e.clientX - rect.left - rect.width / 2;
        const y = e.clientY - rect.top - rect.height / 2;
        const angle = Math.atan2(y, x) * (180 / Math.PI) + 90;
        const hue = angle < 0 ? angle + 360 : angle;
        onChange(`hsl(${Math.round(hue)}, 70%, 50%)`);
    };

    const displayColor = color || '#3B82F6';

    return (
        <div className="ts-cp-container" ref={pickerRef}>
            <div className="ts-cp-input" onClick={() => setOpen(!open)}>
                <div className="ts-cp-swatch" style={{ background: displayColor }} />
                <span className="ts-cp-hex">{displayColor.toUpperCase()}</span>
                <Icon d={icons.chevronDown} size={16} />
            </div>

            {open && (
                <div className="ts-cp-popover">
                    <div className="ts-cp-wheel-space">
                        <div className="ts-cp-wheel" onClick={handleWheelClick}>
                            <div className="ts-cp-wheel-inner" />
                            <div className="ts-cp-selector" style={{
                                transform: `rotate(0deg) translate(0, -40px)`
                            }} />
                        </div>
                    </div>
                    <div className="ts-cp-footer">
                        <input
                            type="text"
                            className="ts-cp-hex-input"
                            value={displayColor}
                            onChange={(e) => onChange(e.target.value)}
                        />
                        <input
                            type="color"
                            className="ts-cp-native"
                            value={displayColor.startsWith('#') ? displayColor : '#3B82F6'}
                            onChange={(e) => onChange(e.target.value)}
                        />
                    </div>
                </div>
            )}
        </div>
    );
}

/* ── Priority Modal ── */
function PriorityModal({ initial, onClose, onSave }) {
    const isEdit = !!initial;
    const [form, setForm] = useState(initial ? { ...initial } : { name: '', color: '#00B2A9', active: true });

    const handleSave = () => {
        if (!form.name.trim()) return;
        onSave(form);
        onClose();
    };

    return (
        <div className="md-modal-overlay" onClick={onClose}>
            <div className="md-modal" onClick={e => e.stopPropagation()}>
                <div className="md-modal-header">
                    <h2 className="md-modal-title">{isEdit ? 'Edit Ticket Priority' : 'Add Ticket Priority'}</h2>
                    <button className="md-modal-close" onClick={onClose}><Icon d={icons.x} size={16} /></button>
                </div>
                <div className="md-modal-body">
                    <div className="md-form-group">
                        <label className="md-label">Priority Label<span className="md-required">*</span></label>
                        <input className="md-input" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g. Critical, High, Low" />
                    </div>

                    <div className="md-form-group" style={{ marginBottom: 24 }}>
                        <label className="md-label">Color Picker</label>
                        <ModernColorPicker color={form.color} onChange={(c) => setForm({ ...form, color: c })} />
                    </div>

                    <div className="md-status-card">
                        <div className="md-status-card-info">
                            <span className="md-status-card-label">Status</span>
                            <span className="md-status-card-desc">{form.active ? 'Visible in ticket portal.' : 'Currently hidden.'}</span>
                        </div>
                        <button className={`md-toggle${form.active ? ' md-toggle-on' : ''}`} onClick={() => setForm({ ...form, active: !form.active })}>
                            <span className="md-toggle-thumb" />
                        </button>
                    </div>
                </div>
                <div className="md-modal-footer">
                    <button className="md-cancel-btn" onClick={onClose}>Cancel</button>
                    <button className="md-save-btn" onClick={handleSave} style={{ background: 'linear-gradient(135deg, #16539C 0%, #7c3aed 100%)', borderRadius: 10 }}>{isEdit ? 'Update Priority' : 'Add Priority'}</button>
                </div>
            </div>
        </div>
    );
}

/* ── Department Modal ── */
function DepartmentModal({ initial, onClose, onSave }) {
    const isEdit = !!initial;
    const [form, setForm] = useState(initial ? { ...initial } : { name: '', manager: '', isActive: true });

    const handleSave = () => {
        if (!form.name.trim()) return;
        onSave(form);
        onClose();
    };

    return (
        <div className="md-modal-overlay" onClick={onClose}>
            <div className="md-modal" onClick={e => e.stopPropagation()}>
                <div className="md-modal-header">
                    <h2 className="md-modal-title">{isEdit ? 'Edit Department' : 'Add Department'}</h2>
                    <button className="md-modal-close" onClick={onClose}><Icon d={icons.x} size={16} /></button>
                </div>
                <div className="md-modal-body">
                    <div className="md-form-group">
                        <label className="md-label">Department Name<span className="md-required">*</span></label>
                        <input className="md-input" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g. Customer Support" />
                    </div>
                    <div className="md-form-group">
                        <label className="md-label">Department Head / Manager</label>
                        <input className="md-input" value={form.manager} onChange={e => setForm({ ...form, manager: e.target.value })} placeholder="Manager name" />
                    </div>
                    <div className="md-status-card" style={{ marginTop: 20 }}>
                        <div className="md-status-card-info">
                            <span className="md-status-card-label">Operational Status</span>
                            <span className="md-status-card-desc">{form.isActive ? 'Department is active.' : 'Department is inactive.'}</span>
                        </div>
                        <button className={`md-toggle${form.isActive ? ' md-toggle-on' : ''}`} onClick={() => setForm({ ...form, isActive: !form.isActive })}>
                            <span className="md-toggle-thumb" />
                        </button>
                    </div>
                </div>
                <div className="md-modal-footer">
                    <button className="md-cancel-btn" onClick={onClose}>Cancel</button>
                    <button className="md-save-btn" onClick={handleSave} style={{ background: 'linear-gradient(135deg, #16539C 0%, #7c3aed 100%)', borderRadius: 10 }}>{isEdit ? 'Update Dept' : 'Add Dept'}</button>
                </div>
            </div>
        </div>
    );
}

/* ── Status Modal ── */
function StatusModal({ initial, onClose, onSave }) {
    const isEdit = !!initial;
    const [form, setForm] = useState(initial ? { ...initial } : { name: '', color: '#22c55e', active: true });

    const handleSave = () => {
        if (!form.name.trim()) return;
        onSave(form);
        onClose();
    };

    return (
        <div className="md-modal-overlay" onClick={onClose}>
            <div className="md-modal" onClick={e => e.stopPropagation()}>
                <div className="md-modal-header">
                    <h2 className="md-modal-title">{isEdit ? 'Edit Ticket Status' : 'Add Ticket Status'}</h2>
                    <button className="md-modal-close" onClick={onClose}><Icon d={icons.x} size={16} /></button>
                </div>
                <div className="md-modal-body">
                    <div className="md-form-group">
                        <label className="md-label">Ticket Status Name<span className="md-required">*</span></label>
                        <input className="md-input" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g. In Progress, Answered" />
                    </div>

                    <div className="md-form-group">
                        <label className="md-label">Pick Color</label>
                        <ModernColorPicker color={form.color} onChange={(c) => setForm({ ...form, color: c })} />


                        <div className="md-status-card" style={{ marginTop: 24 }}>
                            <div className="md-status-card-info">
                                <span className="md-status-card-label">Status Visibility</span>
                                <span className="md-status-card-desc">{form.active ? 'Visible in status lists.' : 'Hidden from lists.'}</span>
                            </div>
                            <button className={`md-toggle${form.active ? ' md-toggle-on' : ''}`} onClick={() => setForm({ ...form, active: !form.active })}>
                                <span className="md-toggle-thumb" />
                            </button>
                        </div>
                    </div>
                    <div className="md-modal-footer">
                        <button className="md-cancel-btn" onClick={onClose}>Close</button>
                        <button className="md-save-btn" onClick={handleSave}>Save</button>
                    </div>
                </div>
            </div>
        </div>
    );
}

/* ── Service Modal ── */
function ServiceModal({ initial, onClose, onSave }) {
    const isEdit = !!initial;
    const [form, setForm] = useState(initial ? { ...initial } : { name: '', active: true });

    const handleSave = () => {
        if (!form.name.trim()) return;
        onSave(form);
        onClose();
    };

    return (
        <div className="md-modal-overlay" onClick={onClose}>
            <div className="md-modal" onClick={e => e.stopPropagation()}>
                <div className="md-modal-header">
                    <h2 className="md-modal-title">{isEdit ? 'Edit Service' : 'Add Service'}</h2>
                    <button className="md-modal-close" onClick={onClose}><Icon d={icons.x} size={16} /></button>
                </div>
                <div className="md-modal-body">
                    <div className="md-form-group">
                        <label className="md-label">Service Name<span className="md-required">*</span></label>
                        <input className="md-input" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g. Consulting, Installation" />
                    </div>

                    <div className="md-status-card" style={{ marginTop: 24 }}>
                        <div className="md-status-card-info">
                            <span className="md-status-card-label">Status</span>
                            <span className="md-status-card-desc">{form.active ? 'Service is active.' : 'Service is inactive.'}</span>
                        </div>
                        <button className={`md-toggle${form.active ? ' md-toggle-on' : ''}`} onClick={() => setForm({ ...form, active: !form.active })}>
                            <span className="md-toggle-thumb" />
                        </button>
                    </div>
                </div>
                <div className="md-modal-footer">
                    <button className="md-cancel-btn" onClick={onClose}>Close</button>
                    <button className="md-save-btn" onClick={handleSave}>Save</button>
                </div>
            </div>
        </div>
    );
}

/* ── Predefined Rich Text Toolbar ── */
function PredefinedRichToolbar({ editorRef }) {
    const exec = (cmd, val = null) => {
        editorRef.current?.focus();
        document.execCommand(cmd, false, val);
    };

    return (
        <div className="et-toolbar" style={{ borderTop: 'none', background: '#fff', padding: '12px 16px', gap: '8px', flexWrap: 'wrap' }}>
            <select className="md-select" style={{ width: 'auto', padding: '4px 8px', fontSize: '14.49px' }} onChange={(e) => exec('fontName', e.target.value)}>
                <option value="Inter">System Font</option>
                <option value="Arial">Arial</option>
                <option value="Times New Roman">Times New Roman</option>
                <option value="Courier New">Courier New</option>
            </select>
            <select className="md-select" style={{ width: 'auto', padding: '4px 8px', fontSize: '14.49px' }} onChange={(e) => exec('fontSize', e.target.value)}>
                <option value="3">12pt</option>
                <option value="1">10pt</option>
                <option value="2">11pt</option>
                <option value="4">14pt</option>
                <option value="5">18pt</option>
                <option value="6">24pt</option>
            </select>

            <span style={{ width: '1px', height: '20px', background: '#e2e8f0', margin: '0 4px' }} />

            <button type="button" className="et-tb-btn" onClick={() => exec('foreColor', prompt('Color Hex:', '#000000'))} title="Text Color"><Icon d={icons.font} size={14} /></button>
            <button type="button" className="et-tb-btn" onClick={() => exec('hiliteColor', prompt('Highlight Hex:', '#ffff00'))} title="Highlight"><Icon d={icons.pencil} size={14} /></button>

            <span style={{ width: '1px', height: '20px', background: '#e2e8f0', margin: '0 4px' }} />

            <button type="button" className="et-tb-btn" onClick={() => exec('bold')} title="Bold"><b>B</b></button>
            <button type="button" className="et-tb-btn" onClick={() => exec('italic')} title="Italic"><i>I</i></button>

            <span style={{ width: '1px', height: '20px', background: '#e2e8f0', margin: '0 4px' }} />

            <button type="button" className="et-tb-btn" onClick={() => exec('justifyLeft')} title="Align Left"><Icon d={icons.alignLeft} size={14} /></button>
            <button type="button" className="et-tb-btn" onClick={() => exec('justifyCenter')} title="Align Center"><Icon d={icons.alignCenter} size={14} /></button>
            <button type="button" className="et-tb-btn" onClick={() => exec('justifyRight')} title="Align Right"><Icon d={icons.alignRight} size={14} /></button>
            <button type="button" className="et-tb-btn" onClick={() => exec('justifyFull')} title="Justify"><Icon d={icons.alignJustify} size={14} /></button>

            <span style={{ width: '1px', height: '20px', background: '#e2e8f0', margin: '0 4px' }} />

            <button type="button" className="et-tb-btn" onClick={() => exec('insertImage', prompt('Image URL:'))} title="Image"><Icon d={icons.image} size={14} /></button>
            <button type="button" className="et-tb-btn" onClick={() => exec('createLink', prompt('URL:'))} title="Link"><Icon d={icons.link} size={14} /></button>
            <button type="button" className="et-tb-btn" title="More"><Icon d={icons.moreHorizontal} size={14} /></button>
        </div>
    );
}

/* ── Redesigned Predefined Reply Modal ── */
function PredefinedReplyModal({ initial, onClose, onSave }) {
    const isEdit = !!initial;
    const [title, setTitle] = useState(initial ? initial.title : '');
    const editorRef = useRef(null);

    useEffect(() => {
        if (initial?.content && editorRef.current) {
            editorRef.current.innerHTML = initial.content;
        }
    }, [initial]);

    const handleSave = () => {
        const content = editorRef.current?.innerHTML || '';
        if (!title.trim() || !content.trim()) return;
        onSave({ ...initial, title, content });
        onClose();
    };

    return (
        <div className="md-modal-overlay" onClick={onClose}>
            <div className="md-modal et-modal-wide" style={{ width: '1000px', background: '#fafafa', borderRadius: '8px' }} onClick={e => e.stopPropagation()}>
                <div className="md-modal-header" style={{ borderBottom: 'none', padding: '24px 32px' }}>
                    <h2 className="md-modal-title" style={{ fontSize: '22.28px', color: '#1f2937', fontWeight: 600 }}>{isEdit ? 'Edit Predefined Reply' : 'Add Predefined Reply'}</h2>
                    <button className="md-modal-close" onClick={onClose}><Icon d={icons.x} size={18} /></button>
                </div>

                <div className="md-modal-body" style={{ padding: '0 32px 32px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    <div style={{ background: '#fff', padding: '32px', borderRadius: '8px', border: '1px solid #f3f4f6', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                        <div className="md-form-group">
                            <label className="md-label" style={{ color: '#1f2937', fontSize: '14.49px', fontWeight: 600, marginBottom: '8px' }}>
                                <span style={{ color: '#ef4444' }}>*</span> Predefined Reply Name
                            </label>
                            <input
                                className="md-input"
                                style={{ borderColor: '#3b82f6', background: '#fff', height: '44px', borderRadius: '6px' }}
                                value={title}
                                onChange={e => setTitle(e.target.value)}
                                placeholder=""
                            />
                        </div>

                        <div className="et-editor-wrap" style={{ marginTop: '24px', border: '1px solid #e1e1e1', borderRadius: '6px', overflow: 'hidden' }}>
                            <PredefinedRichToolbar editorRef={editorRef} />
                            <div
                                ref={editorRef}
                                className="et-editor"
                                contentEditable
                                suppressContentEditableWarning
                                style={{
                                    minHeight: '350px',
                                    borderTop: '1px solid #e1e1e1',
                                    padding: '24px',
                                    background: '#fff',
                                    color: '#333',
                                    fontSize: '15.60px'
                                }}
                            />
                        </div>
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '8px' }}>
                        <button
                            className="md-save-btn"
                            onClick={handleSave}
                            style={{
                                background: '#1c2434',
                                color: '#fff',
                                borderRadius: '4px',
                                padding: '10px 24px',
                                fontSize: '15.60px',
                                fontWeight: 600,
                                border: 'none'
                            }}
                        >
                            Save
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

/* ── Delete Modal ── */

/* ── Delete Modal ── */
function DeleteModal({ itemName, onClose, onConfirm }) {
    return (
        <div className="md-modal-overlay" onClick={onClose}>
            <div className="md-modal md-modal-sm" onClick={e => e.stopPropagation()}>
                <div className="md-modal-header">
                    <h2 className="md-modal-title">Confirm Deletion</h2>
                    <button className="md-modal-close" onClick={onClose}><Icon d={icons.x} size={16} /></button>
                </div>
                <div className="md-modal-body md-delete-body">
                    <div className="md-delete-icon"><Icon d={icons.alertTriangle} size={28} /></div>
                    <p className="md-delete-msg">Delete <strong>"{itemName}"</strong>?<br /><span className="md-delete-sub">Action cannot be undone.</span></p>
                </div>
                <div className="md-modal-footer">
                    <button className="md-cancel-btn" onClick={onClose}>Cancel</button>
                    <button className="md-delete-btn" onClick={() => { onConfirm(); onClose(); }}>Delete Forever</button>
                </div>
            </div>
        </div>
    );
}

export default function TicketSettings() {
    const [activeTab, setActiveTab] = useState('priority');
    const [priorities, setPriorities] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [replies, setReplies] = useState([]);
    const [statuses, setStatuses] = useState([]);
    const [services, setServices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');

    const [modalOpen, setModalOpen] = useState(false);
    const [editItem, setEditItem] = useState(null);
    const [deleteItem, setDeleteItem] = useState(null);

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        setLoading(true);
        try {
            const [pRes, dRes, rRes, sRes] = await Promise.allSettled([
                axios.get(MASTER_DATA_API),
                axios.get(DEPARTMENTS_API),
                axios.get(PREDEFINED_REPLIES_API),
                axios.get(SERVICES_API)
            ]);

            if (pRes.status === 'fulfilled') {
                setPriorities(pRes.value.data.filter(i => i.category === 'ticket-priority'));
                setStatuses(pRes.value.data.filter(i => i.category === 'ticket-status'));
            } else {
                console.error("Fetch MasterData error:", pRes.reason);
            }

            if (dRes.status === 'fulfilled') {
                setDepartments(dRes.value.data);
            } else {
                console.error("Fetch Departments error:", dRes.reason);
            }

            if (rRes.status === 'fulfilled') {
                setReplies(rRes.value.data);
            } else {
                console.error("Fetch Replies error:", rRes.reason);
            }

            if (sRes.status === 'fulfilled') {
                setServices(sRes.value.data);
            } else {
                console.error("Fetch Services error:", sRes.reason);
            }
        } catch (err) {
            console.error("Fetch Ticket Settings error:", err);
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async (formData) => {
        try {
            if (activeTab === 'priority') {
                if (editItem) {
                    const res = await axios.put(`${MASTER_DATA_API}/${editItem._id}`, formData);
                    setPriorities(prev => prev.map(p => p._id === editItem._id ? res.data : p));
                } else {
                    const res = await axios.post(MASTER_DATA_API, { ...formData, category: 'ticket-priority' });
                    setPriorities(prev => [res.data, ...prev]);
                }
            } else if (activeTab === 'department') {
                if (editItem) {
                    const res = await axios.put(`${DEPARTMENTS_API}/${editItem._id}`, formData);
                    setDepartments(prev => prev.map(d => d._id === editItem._id ? res.data : d));
                } else {
                    const res = await axios.post(DEPARTMENTS_API, formData);
                    setDepartments(prev => [res.data.department, ...prev]);
                }
            } else if (activeTab === 'replies') {
                if (editItem) {
                    const res = await axios.put(`${PREDEFINED_REPLIES_API}/${editItem._id}`, formData);
                    setReplies(prev => prev.map(r => r._id === editItem._id ? res.data : r));
                } else {
                    const res = await axios.post(PREDEFINED_REPLIES_API, formData);
                    setReplies(prev => [res.data, ...prev]);
                }
            } else if (activeTab === 'status') {
                if (editItem) {
                    const res = await axios.put(`${MASTER_DATA_API}/${editItem._id}`, formData);
                    setStatuses(prev => prev.map(s => s._id === editItem._id ? res.data : s));
                } else {
                    const res = await axios.post(MASTER_DATA_API, { ...formData, category: 'ticket-status' });
                    setStatuses(prev => [...prev, res.data]);
                }
            } else if (activeTab === 'services') {
                if (editItem) {
                    const res = await axios.put(`${SERVICES_API}/${editItem._id}`, formData);
                    setServices(prev => prev.map(s => s._id === editItem._id ? res.data : s));
                } else {
                    const res = await axios.post(SERVICES_API, formData);
                    setServices(prev => [res.data, ...prev]);
                }
            }
            setModalOpen(false);
            setEditItem(null);
        } catch (err) {
            console.error("Save error:", err);
        }
    };

    const handleDelete = async () => {
        try {
            if (activeTab === 'priority') {
                await axios.delete(`${MASTER_DATA_API}/${deleteItem._id}`);
                setPriorities(prev => prev.filter(p => p._id !== deleteItem._id));
            } else if (activeTab === 'department') {
                await axios.delete(`${DEPARTMENTS_API}/${deleteItem._id}`);
                setDepartments(prev => prev.filter(d => d._id !== deleteItem._id));
            } else if (activeTab === 'replies') {
                await axios.delete(`${PREDEFINED_REPLIES_API}/${deleteItem._id}`);
                setReplies(prev => prev.filter(r => r._id !== deleteItem._id));
            } else if (activeTab === 'status') {
                await axios.delete(`${MASTER_DATA_API}/${deleteItem._id}`);
                setStatuses(prev => prev.filter(s => s._id !== deleteItem._id));
            } else if (activeTab === 'services') {
                await axios.delete(`${SERVICES_API}/${deleteItem._id}`);
                setServices(prev => prev.filter(s => s._id !== deleteItem._id));
            }
            setDeleteItem(null);
        } catch (err) {
            console.error("Delete error:", err);
        }
    };

    const activeList = activeTab === 'priority' ? priorities : activeTab === 'department' ? departments : activeTab === 'status' ? statuses : activeTab === 'services' ? services : replies;
    const filteredItems = activeList.filter(i =>
        (i.name || i.title || '').toLowerCase().includes(search.toLowerCase())
    );

    return (
        <div className="ts-container">
            {/* Sidebar */}
            <aside className="ts-sidebar">
                <div className="ts-nav-item active" style={{ marginBottom: 24, cursor: 'default', background: 'none', color: '#1e293b', paddingLeft: 8 }}>
                    <span style={{ fontSize: 13, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', opacity: 0.5 }}>Support Configuration</span>
                </div>
                <div className={`ts-nav-item ${activeTab === 'priority' ? 'active' : ''}`} onClick={() => setActiveTab('priority')}>
                    <Icon d={icons.flag} size={18} />
                    <span>Ticket Priorities</span>
                </div>
                <div className={`ts-nav-item ${activeTab === 'department' ? 'active' : ''}`} onClick={() => setActiveTab('department')}>
                    <Icon d={icons.department} size={18} />
                    <span>Departments</span>
                </div>
                <div className={`ts-nav-item ${activeTab === 'replies' ? 'active' : ''}`} onClick={() => setActiveTab('replies')}>
                    <Icon d={icons.messageSquare} size={18} />
                    <span>Predefined Replies</span>
                </div>
                <div className={`ts-nav-item ${activeTab === 'status' ? 'active' : ''}`} onClick={() => setActiveTab('status')}>
                    <Icon d={icons.list} size={18} />
                    <span>Ticket Statuses</span>
                </div>
                <div className={`ts-nav-item ${activeTab === 'services' ? 'active' : ''}`} onClick={() => setActiveTab('services')}>
                    <Icon d={icons.wrench} size={18} />
                    <span>Services</span>
                </div>
            </aside>

            {/* Main Content */}
            <main className="ts-main">
                <header className="ts-header">
                    <div className="ts-title-group">
                        <h1>
                            {activeTab === 'priority' ? 'Ticket Priorities' :
                                activeTab === 'status' ? 'Ticket Statuses' :
                                    activeTab === 'department' ? 'Departments' :
                                        activeTab === 'services' ? 'Services' : 'Predefined Replies'}
                        </h1>
                        <p>
                            {activeTab === 'priority' ? 'Manage how tickets are ranked and prioritized.' :
                                activeTab === 'status' ? 'Define different stages for ticket lifecycle.' :
                                    activeTab === 'department' ? 'Define your company structure and department heads.' :
                                        activeTab === 'services' ? 'Manage the services offered in tickets.' :
                                            'Save time with snippets and template responses.'}
                        </p>
                    </div>
                    <div className="ts-controls">
                        <div className="md-search-wrap" style={{ margin: 0 }}>
                            <Icon d={icons.search} size={14} />
                            <input className="md-search" placeholder="Quick search..." value={search} onChange={e => setSearch(e.target.value)} />
                        </div>
                        <button className="md-add-btn" onClick={() => { setEditItem(null); setModalOpen(true); }} style={{ background: 'linear-gradient(135deg, #063A5C 0%, #312e81 100%)' }}>
                            <Icon d={icons.plus} size={14} /> Add {
                                activeTab === 'priority' ? 'Priority' :
                                    activeTab === 'status' ? 'Status' :
                                        activeTab === 'department' ? 'Department' :
                                            activeTab === 'services' ? 'Service' : 'Reply'
                            }
                        </button>
                    </div>
                </header>

                {loading ? (
                    <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>Loading configurations...</div>
                ) : (
                    <div className="md-panel" style={{ background: '#fff', borderRadius: 16, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                        <div className="md-table-wrap">
                            <table className="md-table">
                                <thead>
                                    <tr>
                                        <th className="md-th">
                                            {activeTab === 'replies' ? 'Reply Title' : 'Name'}
                                        </th>
                                        {activeTab === 'department' && <th className="md-th">Manager</th>}
                                        {activeTab === 'replies' && <th className="md-th">Content Preview</th>}
                                        {(activeTab === 'priority' || activeTab === 'department' || activeTab === 'status' || activeTab === 'services') && <th className="md-th">Status</th>}
                                        <th className="md-th">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {loading ? (
                                        <tr><td colSpan={5} className="md-td" style={{ textAlign: 'center', padding: 40 }}>Loading configurations...</td></tr>
                                    ) : filteredItems.length === 0 ? (
                                        <tr>
                                            <td colSpan={5} className="md-td" style={{ textAlign: 'center', padding: 60 }}>
                                                <Icon d={icons[activeTab === 'priority' ? 'flag' : activeTab === 'department' ? 'department' : 'messageSquare']} size={48} strokeWidth={1} style={{ opacity: 0.2, marginBottom: 12 }} />
                                                <p style={{ color: '#94a3b8' }}>No items found in this category.</p>
                                            </td>
                                        </tr>
                                    ) : (
                                        filteredItems.map(item => (
                                            <tr key={item._id} className="md-tr">
                                                <td className="md-td">
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                                        {(activeTab === 'priority' || activeTab === 'status') && (
                                                            <span className="md-color-dot" style={{ background: item.color, width: 10, height: 10, borderRadius: '50%' }} />
                                                        )}
                                                        {activeTab === 'department' && (
                                                            <div style={{ color: '#64748b' }}><Icon d={icons.department} size={14} /></div>
                                                        )}
                                                        <strong style={{ color: '#1e293b' }}>{item.name || item.title}</strong>
                                                    </div>
                                                </td>

                                                {activeTab === 'department' && (
                                                    <td className="md-td">
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#64748b', fontSize: 13 }}>
                                                            <Icon d={icons.user} size={12} />
                                                            {item.manager || 'Unassigned'}
                                                        </div>
                                                    </td>
                                                )}

                                                {activeTab === 'replies' && (
                                                    <td className="md-td">
                                                        <div
                                                            style={{
                                                                fontSize: 12,
                                                                color: '#94a3b8',
                                                                maxWidth: 400,
                                                                whiteSpace: 'nowrap',
                                                                overflow: 'hidden',
                                                                textOverflow: 'ellipsis'
                                                            }}
                                                            dangerouslySetInnerHTML={{ __html: item.content }}
                                                        />
                                                    </td>
                                                )}

                                                {(activeTab === 'priority' || activeTab === 'department' || activeTab === 'status' || activeTab === 'services') && (
                                                    <td className="md-td">
                                                        <span className={`md-status-badge ${(item.active !== false && item.isActive !== false) ? 'md-badge-active' : 'md-badge-inactive'}`}>
                                                            {(item.active !== false && item.isActive !== false) ? 'Active' : 'Inactive'}
                                                        </span>
                                                    </td>
                                                )}

                                                <td className="md-td md-actions">
                                                    <button className="md-icon-btn" onClick={() => { setEditItem(item); setModalOpen(true); }} title="Edit"><Icon d={icons.edit} size={14} /></button>
                                                    <button className="md-icon-btn md-icon-btn-danger" onClick={() => setDeleteItem(item)} title="Delete"><Icon d={icons.trash} size={14} /></button>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </main>

            {modalOpen && (
                activeTab === 'priority' ?
                    <PriorityModal initial={editItem} onClose={() => setModalOpen(false)} onSave={handleSave} /> :
                    activeTab === 'department' ?
                        <DepartmentModal initial={editItem} onClose={() => setModalOpen(false)} onSave={handleSave} /> :
                        activeTab === 'status' ?
                            <StatusModal initial={editItem} onClose={() => setModalOpen(false)} onSave={handleSave} /> :
                            activeTab === 'services' ?
                                <ServiceModal initial={editItem} onClose={() => setModalOpen(false)} onSave={handleSave} /> :
                                <PredefinedReplyModal initial={editItem} onClose={() => setModalOpen(false)} onSave={handleSave} />
            )}

            {deleteItem && (
                <DeleteModal itemName={deleteItem.name} onClose={() => setDeleteItem(null)} onConfirm={handleDelete} />
            )}
        </div>
    );
}
